import { getToken, onMessage, deleteToken } from "firebase/messaging";
import { getFirebaseMessaging } from "./firebase";
import apiRequest from "@/services/apiRequest";
import store from "@/store";

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY;
const REGISTER_ENDPOINT =
  process.env.VUE_APP_FCM_REGISTER_ENDPOINT || "/save-fcm-token";
const UNREGISTER_ENDPOINT =
  process.env.VUE_APP_FCM_UNREGISTER_ENDPOINT || "/un-subscribe-fcm-token";
const DEFAULT_ICON = "/razcofood-favicon.png";


// Debug logging for missing VAPID key
if (!VAPID_KEY) {
  console.error(
    "🚨 VUE_APP_FIREBASE_VAPID_KEY is missing! Firebase notifications will not work."
  );
  console.error("Please add VUE_APP_FIREBASE_VAPID_KEY to your .env file.");
  console.error(
    "Get the VAPID key from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates"
  );
}

let foregroundUnsubscribe = null;
let swMessageListenerAttached = false;
let notificationAudio = null;
let notificationAudioReady = null;

function normalizeNotificationPayload(payload) {
  const notification = payload?.notification || {};
  const customData = payload?.data || {};
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  const title =
    customData.title || notification.title || "Notification";
  const body = customData.body || notification.body || "";
  const icon =
    customData.icon || notification.icon || DEFAULT_ICON;
  const tag =
    customData.tag ||
    notification.tag ||
    (payload?.messageId ? `fcm-${payload.messageId}` : "firebase-notification");

  const targetUrl =
    customData.url ||
    customData.link ||
    customData.click_action ||
    notification.click_action ||
    (customData.invoice_id
      ? `${(customData.domain || notification.domain || origin).replace(
          /\/$/,
          ""
        )}/invoice/${customData.invoice_id}`
      : customData.domain || notification.domain || origin);

  return {
    title,
    body,
    icon,
    tag,
    targetUrl,
    data: {
      ...customData,
      _url: targetUrl,
      _messageId: payload?.messageId,
      _from: payload?.from,
    },
  };
}

async function showBrowserNotification(messageDetails) {
  if (typeof window === "undefined") return false;
  if (!messageDetails?.title && !messageDetails?.body) return false;

  const { title, body, icon, tag, data } = messageDetails;
  const notificationOptions = {
    body,
    icon,
    tag,
    renotify: true,
    data,
  };

  if ("serviceWorker" in navigator) {
    try {
      const registration =
        (await navigator.serviceWorker.getRegistration()) ||
        (await navigator.serviceWorker.ready);

      if (registration && typeof registration.showNotification === "function") {
        await registration.showNotification(title, notificationOptions);
        return true;
      }
    } catch (err) {
      console.warn("Service worker showNotification failed", err);
    }
  }

  if ("Notification" in window && Notification.permission === "granted") {
    try {
      const notification = new Notification(title, notificationOptions);
      notification.onclick = function (event) {
        event.preventDefault();
        const url = this.data?._url;
        if (url) {
          const absolute = url.startsWith("/")
            ? new URL(url, window.location.origin).href
            : url;
          window.focus();
          window.open(absolute, "_blank", "noopener,noreferrer");
        }
        this.close();
      };
      return true;
    } catch (err) {
      console.warn("Window notification failed", err);
    }
  }

  return false;
}

function ensureNotificationAudio() {
  if (notificationAudio || typeof window === "undefined")
    return notificationAudio;

  notificationAudio = new Audio("/sounds/notification-sound-1.mp3");
  notificationAudio.preload = "auto";
  notificationAudio.volume = 1;

  notificationAudioReady = new Promise((resolve, reject) => {
    const onReady = () => {
      notificationAudio.removeEventListener("canplaythrough", onReady);
      notificationAudio.removeEventListener("error", onError);
      resolve();
    };
    const onError = (event) => {
      notificationAudio.removeEventListener("canplaythrough", onReady);
      notificationAudio.removeEventListener("error", onError);
      reject(event?.error || new Error("Failed to load notification sound"));
    };

    notificationAudio.addEventListener("canplaythrough", onReady, {
      once: true,
    });
    notificationAudio.addEventListener("error", onError, { once: true });
  }).catch((err) => {
    console.warn("Notification sound preload failed", err);
  });

  return notificationAudio;
}

export async function playNotificationSound() {
  try {
    const audio = ensureNotificationAudio();
    if (!audio) return;

    // Wait for the audio element to be ready if we haven't already
    if (notificationAudioReady) {
      await notificationAudioReady;
    }

    // Restart the clip from the beginning each time
    audio.currentTime = 0;
    await audio.play();
  } catch (err) {
    console.warn("Notification sound failed", err);
  }
}

function attachServiceWorkerMessageListener() {
  if (swMessageListenerAttached) return;
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    console.log("🔔 Service worker message received:", event);
    if (event?.data?.type !== "FCM_BACKGROUND_NOTIFICATION") return;
    playNotificationSound();
  });
  swMessageListenerAttached = true;
}

export async function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );
      return reg;
    } catch (e) {
      console.warn("FCM SW registration failed", e);
    }
  }
  return null;
}

export async function requestPermission() {
  if (!("Notification" in window)) return false;
  const current = Notification.permission;
  if (current === "granted") return true;
  if (current === "denied") return false;
  const perm = await Notification.requestPermission();
  return perm === "granted";
}

export async function getAndRegisterFcmToken() {
  try {
    // Check for VAPID key first
    if (!VAPID_KEY) {
      console.error("❌ Cannot generate FCM token: VAPID key is missing");
      return null;
    }

    const permission = await requestPermission();
    if (!permission) {
      console.warn("❌ Notification permission denied");
      return null;
    }

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      console.error("❌ Firebase messaging not supported");
      return null;
    }

    const swReg = await registerServiceWorker();
    if (!swReg) {
      console.warn("⚠️ Service worker registration failed");
    }

    console.log("🔑 Generating FCM token...");
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg || undefined,
    });

    if (token) {
      console.log(
        "✅ FCM token generated successfully:",
        token.substring(0, 20) + "..."
      );

      try {
        localStorage.setItem("fcm_token", token);
      } catch (_) {
        void 0; // ignore storage errors
      }

      try {
        console.log("📤 Registering token with backend...");
        await new Promise((resolve, reject) => {
          apiRequest.backofficePost(REGISTER_ENDPOINT, { token }, (res) => {
            if (res?.status && res.status < 400) {
              console.log("✅ Token registered with backend successfully");
              resolve(res);
            } else {
              console.error("❌ Backend registration failed:", res);
              reject(res);
            }
          });
        });
      } catch (err) {
        // Non-fatal for client registration
        console.warn("⚠️ Failed to register FCM token with backend", err);
      }
    } else {
      console.error("❌ Failed to generate FCM token");
    }
    return token;
  } catch (err) {
    console.error("❌ FCM token error:", err);
    return null;
  }
}

export async function unregisterFcmToken() {
  try {
    const messaging = await getFirebaseMessaging();

    // There is no direct getCurrentToken without a call; deleteToken will clear the default token
    // Attempt to notify backend best-effort (without token lookup)
    try {
      const token = localStorage.getItem("fcm_token");
      if (token) {
        await new Promise((resolve) => {
          apiRequest.backofficePost(UNREGISTER_ENDPOINT, { token }, () =>
            resolve()
          );
        });
      }
    } catch (_) {
      void 0; // ignore backend unregister errors
    }

    if (messaging) {
      await deleteToken(messaging);
    }
    try {
      localStorage.removeItem("fcm_token");
    } catch (_) {
      void 0; // ignore storage errors
    }
  } catch (err) {
    console.warn("FCM unregister error", err);
  }
}

let vueRootInstance = null;

export function setVueRootInstance(instance) {
  vueRootInstance = instance;
}

export async function attachForegroundHandler() {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return;

  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
    foregroundUnsubscribe = null;
  }

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    console.log("🔔 Foreground FCM payload received:", payload);
    try {
      const normalized = normalizeNotificationPayload(payload);

      if ("Notification" in window && Notification.permission === "granted") {
        showBrowserNotification(normalized)
          .then((displayed) => {
            if (displayed) playNotificationSound();
          })
          .catch((err) => {
            console.warn("Failed to render foreground notification", err);
          });
      }

      // Emit event to Vue components if root instance is available
      if (vueRootInstance) {
        console.log("✅ Emitting fcm-message-received event to Vue components");
        vueRootInstance.$root.$emit("fcm-message-received", payload);
      } else {
        console.warn("⚠️ Vue root instance not available for event emission");
      }

      // Optional: show toast via store
      store.commit("setToastData", {
        message:
          normalized.title || normalized.body || "New notification",
        color: "success",
        snackbar: true,
      });
    } catch (e) {
      console.warn("Error handling foreground message", e);
    }
  });
}

export async function initNotificationsForSession() {
  await registerServiceWorker();
  attachServiceWorkerMessageListener();
  await attachForegroundHandler();
}
